import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Filters,
  Page,
  PageHeader,
  Table,
} from '@evogard/ui';
import type { Event } from '@evogard/contract';
import { formatInstant } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

/**
 * A trilha de auditoria: quem fez o quê, quando.
 *
 * O que os sete módulos registram passa a ser lido por quem administra, e não
 * só por quem tem acesso ao `psql`.
 *
 * ## Paginação por chave, e por isso "carregar mais" e não páginas numeradas
 *
 * A tabela só cresce. Com deslocamento, uma linha nova chegando entre duas
 * páginas empurra o que ainda não foi lido — e um item some da listagem sem
 * ninguém ter apagado nada. O servidor devolve `proximo`, que é o id da última
 * linha lida; a próxima busca continua dali.
 *
 * Botão e não rolagem infinita: a trilha é lida para procurar uma coisa
 * específica, e rolagem infinita rouba o rodapé de quem quer parar.
 *
 * ## O que esta tela não faz
 *
 * **Não traduz `tipo` nem `entidade`.** Os sete módulos escrevem os próprios
 * valores, e um dicionário aqui envelheceria a cada evento novo — mostrando
 * "usuario_bloqueado" para uns e um rótulo bonito para outros, sem que dê para
 * saber qual dos dois está desatualizado. O valor cru é feio e é verdadeiro.
 */
export function AuditScreen() {
  const { data: context } = useSessionContext();
  /*
   * O recorte APLICADO, num objeto só — e é o painel de `Filters` que o
   * confirma.
   *
   * **A espera pela digitação saiu junto, e não foi uma perda.** Os dois campos
   * livres eram debounced porque cada tecla entrava na chave de uma consulta
   * INFINITA sobre `app.events` — a tabela que `docs/03-estrutura-e-dados.html`
   * registra como a que caminha para partição: digitar `usuario_bloqueado`
   * disparava dezessete buscas de 50 linhas cada, nenhuma cancelando a
   * anterior. O painel resolve o mesmo problema de um jeito mais forte: a
   * consulta sai UMA vez, no botão, com os quatro campos juntos.
   */
  const [filters, setFilters] = useState({
    entity: '',
    tipo: '',
    from: '',
    until: '',
  });
  const { entity, tipo, from, until } = filters;

  const filter = {
    ...(entity ? { entity } : {}),
    ...(tipo ? { tipo } : {}),
    ...(from ? { de: from } : {}),
    ...(until ? { until } : {}),
  };

  const auditQuery = useInfiniteQuery({
    /*
     * A BASE entra na chave, como em toda lista escopada por tenant.
     *
     * Sem ela, trocar de base no seletor da moldura mantinha as linhas da base
     * anterior na tela enquanto o refetch de fundo não chegava — a trilha da CE
     * sob um cabeçalho que já diz "o que aconteceu nesta base" para a GO. Numa
     * tela que existe para investigar, é o lugar mais caro da casa para mostrar
     * a linha de uma praça com o nome de outra.
     */
    queryKey: ['plataforma', 'eventos', context?.currentTenant.id, filter],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const r = await api.platform.listEvents({
        query: { ...filter, limit: 50, ...(pageParam ? { beforeOf: pageParam } : {}) },
      });
      return ok(r, 200, 'Não foi possível carregar a trilha.');
    },
    getNextPageParam: (last) => last.next ?? undefined,
  });

  const events = auditQuery.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Page measure="cheia">
      <PageHeader title="Trilha de auditoria">
        O que aconteceu nesta base, do mais recente para o mais antigo.
      </PageHeader>

      {auditQuery.isError && (
        <Alert tone="erro" title="Não foi possível carregar a trilha.">
          Recarregue a página para tentar de novo.
        </Alert>
      )}

      {/*
       * Erro sem evento nenhum: o aviso acima é a resposta inteira.
       *
       * Numa trilha de auditoria a afirmação errada é a mais cara de todas: com
       * a tabela aqui, ela dizia "Os atos registrados nesta base aparecem aqui"
       * logo abaixo de "não foi possível carregar a trilha" — quem estivesse
       * investigando leria que não há registro, e é justamente a hora em que
       * ninguém pode ler isso.
       */}
      {(!auditQuery.isError || events.length > 0) && (
        <Card bleed>
          <Table
            /*
             * Os quatro recortes no painel, e nenhum campo de busca na barra.
             *
             * Eles eram um `Card title="Filtros"` de quatro campos entre o
             * título e a tabela — a forma que o resto da casa abandonou em
             * 17/09. E nenhum deles é "a busca": `entity` e `tipo` casam contra
             * colunas diferentes, e eleger um dos dois para a lupa faria a barra
             * prometer uma busca geral que a rota não tem.
             *
             * `Select` com as entidades que já existem seria uma lista para
             * manter em acordo com sete módulos. Campo livre é honesto: quem usa
             * a trilha sabe o nome do que procura, e o que ele digitar errado
             * devolve vazio em vez de mentir.
             *
             * Os dois `placeholder` ficam em MINÚSCULA, contra a régua dos
             * outros: eles não descrevem o que digitar, eles mostram o VALOR
             * gravado. `usuario_bloqueado` é o tipo do evento como ele está na
             * coluna, e o campo casa por texto — escrito `Usuario_bloqueado` o
             * exemplo deixaria de existir na trilha, e quem o copiasse receberia
             * vazio sem entender por quê.
             */
            toolbar={
              <Filters
                values={filters}
                onApply={(values) =>
                  setFilters({
                    entity: String(values.entity ?? ''),
                    tipo: String(values.tipo ?? ''),
                    from: String(values.from ?? ''),
                    until: String(values.until ?? ''),
                  })
                }
                fields={[
                  {
                    type: 'intervalo',
                    label: 'Ocorrido em',
                    from: 'from',
                    to: 'until',
                    help: 'O dia inteiro entra, nos dois extremos.',
                  },
                  {
                    type: 'texto',
                    name: 'entity',
                    label: 'Entidade',
                    placeholder: 'usuario, ticket…',
                  },
                  {
                    type: 'texto',
                    name: 'tipo',
                    label: 'Tipo',
                    placeholder: 'usuario_bloqueado…',
                  },
                ]}
              />
            }
            label="Trilha de auditoria"
            items={events}
            loading={auditQuery.isLoading}
            rowKey={(e) => e.id}
            empty={
              entity || tipo || from || until
                ? 'Nenhum evento com esses filtros nesta base.'
                : 'Os atos registrados nesta base aparecem aqui.'
            }
            columns={[
              {
                title: 'Quando',
                minWidth: '9rem',
                cell: (e: Event) => (
                  <span className="tabular-nums">{formatInstant(e.occurredAt)}</span>
                ),
              },
              {
                title: 'Quem',
                alignment: 'inicio' as const,
                cell: (e: Event) => (
                  <span className="flex items-center gap-2">
                    <Badge tone={e.actorType === 'usuario' ? 'neutro' : 'informacao'}>
                      {e.actorType}
                    </Badge>
                    {/*
                     * O nome quando ele existe, o id quando não.
                     *
                     * A trilha guarda o id — ela registra quem era no momento do
                     * ato, e o nome muda. Mas mostrar só o id fazia esta coluna
                     * não responder à própria pergunta: "quem fez isso" virava um
                     * hash de 32 caracteres, e a coluna ainda truncava no meio.
                     * O `title` guarda o id inteiro para quem precisar dele.
                     */}
                    <span className="truncate" title={e.actorId ?? undefined}>
                      {e.actorName ?? (
                        <span className="text-xs text-texto-suave">
                          {e.actorId ?? '—'}
                        </span>
                      )}
                    </span>
                  </span>
                ),
              },
              {
                title: 'O quê',
                cell: (e: Event) => <span className="font-medium">{e.tipo}</span>,
              },
              {
                title: 'Sobre',
                alignment: 'inicio' as const,
                cell: (e: Event) => (
                  /*
                   * O id da entidade vai no `title`, não na linha: ele é longo,
                   * truncava no meio e um id cortado não serve para nada — é
                   * decoração ocupando a largura que o nome da entidade precisa.
                   */
                  <span className="text-texto-suave" title={e.entityId}>
                    {e.entity}
                  </span>
                ),
              },
            ]}
          />
        </Card>
      )}

      {auditQuery.hasNextPage && (
        <div>
          <Button
            variant="contorno"
            loading={auditQuery.isFetchingNextPage}
            onClick={() => void auditQuery.fetchNextPage()}
          >
            Carregar mais
          </Button>
        </div>
      )}
    </Page>
  );
}
